/**
 * Shared shell for the public legal pages (privacy policy, terms of use).
 *
 * They are the two documents both stores demand a working URL for, a reviewer
 * opens them with no session and no app installed, and they cross-link to each
 * other — so they share one layout rather than drifting apart in two copies.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function LegalDoc(props: {
  title: string;
  updated: string;
  /** The sibling document, linked at the foot so a reviewer finds both. */
  sibling: { href: string; label: string };
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <main className="mx-auto max-w-2xl px-6 py-12 text-ink">
      <h1 className="font-display text-[34px] font-medium leading-tight tracking-[-0.02em]">
        {props.title}
      </h1>
      <p className="mt-2 text-sm text-ink-muted">{t('legal.updated', { date: props.updated })}</p>
      {props.children}
      <footer className="mt-10 border-t border-hairline pt-4 text-[15px] text-ink-dim">
        {t('legal.seeAlso')}{' '}
        <Link className="text-accent underline" to={props.sibling.href}>
          {props.sibling.label}
        </Link>
      </footer>
    </main>
  );
}

export function Section(props: { title: string; id?: string; children: React.ReactNode }) {
  return (
    // `id` exists so a store form can link straight at a section — Google Play
    // wants a URL that lands on the deletion instructions, not just the policy.
    <section id={props.id} className="mt-8 scroll-mt-8">
      <h2 className="text-[17px] font-medium text-ink">{props.title}</h2>
      <div className="mt-2 space-y-1 text-[15px] leading-relaxed text-ink-dim">{props.children}</div>
    </section>
  );
}

export function List(props: { items: [string, string][] }) {
  return (
    <ul className="space-y-2">
      {props.items.map(([term, rest]) => (
        <li key={term} className="flex gap-2">
          <span aria-hidden className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-ink-muted" />
          <span>
            <b className="font-medium text-ink">{term}</b> — {rest}
          </span>
        </li>
      ))}
    </ul>
  );
}
